import React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import { produce } from 'immer';
import { GoogleLoginResponse } from 'react-google-login';
import {
    VerificationStepsEnum,
    MilitaryStatusDefaultMessagesEnum,
    isFormFilled,
    hasFailedInstantMatch,
    setRef,
    resetRefs,
    logger,
    VerificationService,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    FormSelectChoice,
    Organization,
    ActiveMilitaryPersonalInfoViewModel,
    ActiveMilitaryPersonalInfoResponse,
    handleEmailOnKeyDown,
    submitForm,
    updateFieldValidationErrorsByFieldId,
    updateViewModelOrganization,
    getAvailableMilitaryStatuses,
    CountryComponentWrapper,
    LogoComponent,
    PhoneNumberComponent as PhoneNumber,
    FirstNameComponent as FirstName,
    LastNameComponent as LastName,
    MarketConsentWrapper,
} from '@sheerid/jslib';

import { FormFooterComponent as FormFooter } from '../../FormFooterComponent';
import { handleOauthOnSuccess } from '../../../lib/utils/stepComponentHelpers';
import { EmailComponent as Email } from '../../FormFields/EmailComponent';
import { GoogleLoginWrapperComponent } from '../../FormFields/GoogleLoginWrapper';
import { BranchOfServiceComponent as BranchOfService } from '../../FormFields/BranchOfServiceComponent';
import { MilitaryStatusComponent as MilitaryStatus } from '../../FormFields/MilitaryStatusComponent';
import { BirthDateComponent as BirthDate } from '../../FormFields/BirthDateComponent';
import { HowDoesVerifyingWorkComponent as HowDoesVerifyingWork } from '../../HowDoesVerifyingWorkComponent';

interface StepActiveMilitaryPersonalInfoComponentProps {
    intl: any;
    verificationService: VerificationService;
}

export const StepActiveMilitaryPersonalInfo = ({ intl, verificationService }: StepActiveMilitaryPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel;
    const previousViewModel = verificationService.previousViewModel as ActiveMilitaryPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as ActiveMilitaryPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const availableStatuses = getAvailableMilitaryStatuses(verificationService, intl);

    const updateActiveMilitaryViewModel = (key: keyof ActiveMilitaryPersonalInfoViewModel, value: any) => {
        const nextState: ActiveMilitaryPersonalInfoViewModel = produce(viewModel, (draft: ActiveMilitaryPersonalInfoViewModel) => {
            (draft[key] as any) = value;

            // If there are no status choices, don't validate
            if (!availableStatuses) {
                draft.fieldsToSkipValidation = ['status'];
            }
        });
        verificationService.updateViewModel(nextState);
    };

    const submitStep = () => {
        const nextState = produce(viewModel, () => {});
        logger.info('StepMilitaryStatusComponent submitting form');
        verificationService.submitStep(
            VerificationStepsEnum.collectMilitaryStatus,
            nextState,
            verificationService.previousVerificationResponse || verificationService.verificationResponse,
        );
    };

    if ((viewModel.status && previousViewModel && previousViewModel.status !== viewModel.status) ||
        (viewModel.status && verificationService.verificationResponse.currentStep === VerificationStepsEnum.collectMilitaryStatus)) {
        submitStep();
    }

    // Refs should be reset to ensure the field order isn't affected by the previous step
    resetRefs();

    return (
        <div id="sid-step-active-military-personal-info" className="sid-l-container">
            { failedInstantMatch ?
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.title" defaultMessage="Check Your Name & Birthdate" />
                    </div>

                    <div className="sid-header__subtitle sid-header__subtitle--error">
                        <FormattedHTMLMessage id="step.personalInfo.tryAgain.subtitle"
                            defaultMessage="
                                Enter your first and last name on the form as it appears on your military document(s).
                                Keep in mind that may be your given name."
                        />
                    </div>
                </div>
            :
                <div className="sid-header">
                    <div className="sid-l-horz-center">
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className="sid-header__title">
                        <FormattedHTMLMessage id="step.personalInfo.military.title" defaultMessage="Unlock this Military-Only Offer" />
                    </div>

                    <div className="sid-header__subtitle">
                        <FormattedHTMLMessage id="step.personalInfo.military.subtitle"
                            defaultMessage="Verify you're a member of the armed forces." />
                    </div>
                    <div className="sid-header__how-verifying-works">
                        <HowDoesVerifyingWork verificationService={verificationService} />
                    </div>
                </div>
            }
            <div className="sid-l-space-top-lg"></div>
            <CountryComponentWrapper
                verificationService={verificationService}
                viewModel={viewModel}
                nextFocusField="status"
            />

            {
                availableStatuses ?
                <MilitaryStatus
                    value={{
                        value: (verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel).status,
                        label: MilitaryStatusDefaultMessagesEnum[(verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel).status],
                    }}
                    isErrored={!!fieldValidationErrors.status}
                    options={ availableStatuses }
                    onChange={
                        (status: FormSelectChoice) => {
                            updateActiveMilitaryViewModel('status', status ? (status.value as string) : '');
                            updateFieldValidationErrorsByFieldId('status', status ? (status.value as string) : '', verificationService);
                        }
                    }
                />
                : null
            }

            <BranchOfService
                autoFocus={viewModel.status && viewModel.organization.id === 0 ? true : false}
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
                value={(verificationService.viewModel as ActiveMilitaryPersonalInfoViewModel).organization}
                verificationService={verificationService}
                organizations={ verificationService.orgList }
            />

            <FirstName
                value={viewModel.firstName}
                isErrored={!!fieldValidationErrors.firstName}
                onChange={
                    (newValue: string) => {
                        updateActiveMilitaryViewModel('firstName', newValue);
                        updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                    }
                }
            />

            <LastName
                value={viewModel.lastName}
                isErrored={!!fieldValidationErrors.lastName}
                onChange={
                    (newValue: string) => {
                        updateActiveMilitaryViewModel('lastName', newValue);
                        updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                    }
                }
            />

            <BirthDate
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                onChange={(newValue: string) => {
                    updateActiveMilitaryViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <div className="sid-google-fi-email">
                <Email
                    value={viewModel.email}
                    isErrored={!!fieldValidationErrors.email}
                    explanation={
                        <FormattedHTMLMessage
                            id="emailExplanation"
                            defaultMessage="Personal email address is recommended"
                        />
                    }
                    onChange={
                        (newValue: string) => {
                            updateActiveMilitaryViewModel('email', newValue);
                            updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                        }
                    }
                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
                    disabled
                />

                <div className="sid-google-fi-email__not-signed-in sid-l-space-top-sm">
                        <FormattedHTMLMessage
                            id="notYourAccount"
                            defaultMessage="Not your Google Fi account?"
                        />
                        &nbsp;
                        <GoogleLoginWrapperComponent
                            onSuccess={(response) => {
                                const newGoogleEmail = (response as GoogleLoginResponse).getBasicProfile().getEmail();
                                handleOauthOnSuccess(response);
                                updateActiveMilitaryViewModel('email', newGoogleEmail);
                            }}
                            buttonComponent={renderProps => (
                                <div
                                    tabIndex={0}
                                    className="sid-link sid-h-link-like"
                                    onClick={() => {
                                        renderProps.onClick();
                                    }}
                                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                                        if (e.key === 'Enter' || e.key === 'Spacebar' || e.key === ' ') {
                                            renderProps.onClick();
                                        }
                                    }}
                                    role="link"
                                >
                                    <FormattedHTMLMessage
                                        id="switchNow"
                                        defaultMessage="Switch now"
                                    />
                                </div>
                            )}
                        />
                    </div>
            </div>

            {
                verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled ?
                    <PhoneNumber
                        isRequired={!!verificationService.programTheme.smsLoopEnabled}
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={
                            (newValue: string) => {
                                updateActiveMilitaryViewModel('phoneNumber', newValue);
                                updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                            }
                        }
                    />
                    : null
            }

            <div className="sid-l-space-top-md">
                <FormattedHTMLMessage id="otherMembers" defaultMessage="Other members of your group plan will get the same international exceptions while you're serving abroad and active on Fi." />
            </div>

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={!!fieldValidationErrors.marketConsentValue}
                onChange={(newValue) => {
                    updateActiveMilitaryViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => submitForm(viewModel, verificationService, VerificationStepsEnum.collectActiveMilitaryPersonalInfo)}
                        type="submit"
                        className={`sid-btn sid-btn--dark sid-l-full-width ${!isFormFilled(viewModel, verificationService.formValidationOptions) ? 'sid-btn--disabled-like' : ''}`}
                        aria-labelledby="verify-status-text"
                        ref={button => setRef('submitButton', button)}
                    >
                        <span id="verify-status-text">
                            { failedInstantMatch ?
                                <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                            :
                                <FormattedHTMLMessage id="verifyMyMilitaryStatus" defaultMessage="Verify My Military Status" />
                            }
                        </span>
                    </button>
                </div>
            </div>

            <hr className="sid-l-horz-separator sid-l-space-top-md"></hr>

            <FormFooter verificationService={verificationService} />
        </div>
    );
};

export const StepActiveMilitaryPersonalInfoComponent = injectIntl(StepActiveMilitaryPersonalInfo);
